import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import { useProductStore } from '../stores/ProductStore';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Typography, Button, CardMedia, Box, Chip, List, ListItem, ListItemText, Divider, useMediaQuery, IconButton, Rating, TextField, Alert, Collapse, CircularProgress } from '@mui/material';
import { KeyboardArrowLeft, KeyboardArrowRight } from '@mui/icons-material';
import PageLayout from '../components/PageLayout';
import cartStore from '../stores/CartStore';
import { useTheme } from '@mui/material/styles';
import { Helmet } from 'react-helmet-async';
import apiHandler from '../handlers/ApiHandler';

const ProductDetailsPage = observer(() => {
  const { id } = useParams();
  const productStore = useProductStore();
  const [selectedImage, setSelectedImage] = useState(null);
  const theme = useTheme();
  const isMdUp = useMediaQuery(theme.breakpoints.up('md'));
  const product = productStore.selectedProduct;
  const [isHovered, setIsHovered] = useState(false);
  const scrollContainerRef = React.useRef(null);
  const navigate = useNavigate();

  const emptySummary = { average: 0, count: 0, distribution: { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 } };
  const [reviews, setReviews] = useState([]);
  const [reviewSummary, setReviewSummary] = useState(emptySummary);
  const [reviewsLoading, setReviewsLoading] = useState(false);
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [reviewForm, setReviewForm] = useState({ authorName: '', rating: 0, title: '', body: '' });
  const [reviewError, setReviewError] = useState('');
  const [reviewSuccess, setReviewSuccess] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    productStore.loadProduct(id);
  }, [id, productStore]);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    setReviewsLoading(true);
    apiHandler
      .get('/reviews/product/' + id)
      .then((data) => {
        if (cancelled) return;
        setReviews((data && data.reviews) || []);
        setReviewSummary((data && data.summary) || emptySummary);
      })
      .catch(() => {
        if (cancelled) return;
        setReviews([]);
        setReviewSummary(emptySummary);
      })
      .finally(() => {
        if (!cancelled) setReviewsLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleReviewSubmit = async (e) => {
    e.preventDefault();
    setReviewError('');
    setReviewSuccess('');
    if (!reviewForm.authorName.trim()) {
      setReviewError('Please enter your name.');
      return;
    }
    if (!reviewForm.rating) {
      setReviewError('Please select a rating.');
      return;
    }
    setSubmitting(true);
    try {
      const res = await apiHandler.post('/reviews', {
        product: id,
        rating: reviewForm.rating,
        title: reviewForm.title,
        body: reviewForm.body,
        authorName: reviewForm.authorName,
      });
      setReviewForm({ authorName: '', rating: 0, title: '', body: '' });
      setShowReviewForm(false);
      setReviewSuccess((res && res.message) || 'Thanks! Your review is awaiting approval.');
    } catch (err) {
      setReviewError(err.message || 'Something went wrong submitting your review.');
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    if (product) {
      const images = [
        ...(product.attachments || []),
        ...(product.primaryImage && !product.attachments?.some((img) => img === product.primaryImage)
          ? [product.primaryImage]
          : []),
      ];
      if (images.length > 0) {
        setSelectedImage(images[0].url);
      }
    }
  }, [product]);

  if (!product) return null;

  const images = [
    ...(product.primaryImage ? [product.primaryImage] : []),
    ...(product.attachments || []),
  ];

  const formatDate = (date) => {
    if (!date) return 'N/A';
    const formattedDate = new Date(date);
    return formattedDate.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const formatKey = (key) => {
    const words = key.match(/[A-Z][a-z]+|[0-9]+|[A-Z]+(?![a-z])/g);
    return words.join(' ');
  };

  const addAndNavigate = () => {
    cartStore.addToCart(product);
    navigate('/cart');
  };

  const renderList = (title, items) => (
    <Box sx={{ mt: 3 }}>
      <Typography variant="h6" gutterBottom>
        {title}
      </Typography>
      <List dense>
        {items &&
          Object.entries(items).map(([key, value], index) => {
            if (typeof value === 'object') {
              const formattedValue = Object.entries(value).map(([innerKey, innerValue]) => (
                <Typography variant="body2" key={innerKey}>
                  <strong>{formatKey(innerKey)}:</strong> {innerValue}
                </Typography>
              ));
              return (
                <ListItem key={index} disableGutters>
                  <ListItemText primary={formattedValue} />
                </ListItem>
              );
            } else {
              return (
                <ListItem key={index} disableGutters>
                  <ListItemText primary={`${formatKey(key)}: ${value}`} />
                </ListItem>
              );
            }
          })}
      </List>
    </Box>
  );

  const renderDimensions = () => {
    const { length, width, height, weight } = product.dimensions || {};
    return (
      <Box sx={{ mt: 3 }}>
        <Typography variant="body2">
          <strong>Dimensions:</strong> {length} x {width} x {height} cm
        </Typography>
        <Typography variant="body2">
          <strong>Weight:</strong> {weight} kg
        </Typography>
      </Box>
    );
  };

  const renderStockStatus = () => {
    if (product.productType.includes('pre-order')) return 'Preorder';
    if (product.stock > 0) return 'In Stock';
    if (product.allowBackorders) return 'Backorder';
    if (product.stock === 0) return 'Out of Stock';
    if (product.productType === 'digital') return 'Digital';
    if (product.productType === 'made-to-order') return 'Made to Order';
    return 'Unavailable';
  };

  const stockStatus = renderStockStatus();
  const isAvailableForOrder = stockStatus !== 'Out of Stock' && stockStatus !== 'Unavailable';

  const handleScroll = (direction) => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({
        left: direction === 'left' ? -100 : 100,
        behavior: 'smooth',
      });
    }
  };

  const formatPrice = (price) => new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: product.currency || 'USD'
  }).format(price);

  const productLd = {
    '@context': 'https://schema.org/',
    '@type': 'Product',
    name: product.name,
    description:
      product.description ||
      (product.descriptionHtml ? product.descriptionHtml.replace(/<[^>]+>/g, '').trim() : ''),
    image: product.primaryImage?.url || product.attachments?.[0]?.url || undefined,
    sku: product.SKU || undefined,
    gtin12: product.UPC || undefined,
    brand: { '@type': 'Brand', name: 'Ear Physics' },
    offers: {
      '@type': 'Offer',
      price: product.price,
      priceCurrency: (product.currency || 'USD').toUpperCase(),
      availability: product.isAvailable
        ? 'https://schema.org/InStock'
        : 'https://schema.org/PreOrder',
      url: `https://earphysics.com/products/${product._id}`,
    },
  };

  if (reviewSummary.count > 0) {
    productLd.aggregateRating = {
      '@type': 'AggregateRating',
      ratingValue: reviewSummary.average,
      reviewCount: reviewSummary.count,
    };
  }

  return (
    <PageLayout pageTitle={product.name}>
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(productLd)}</script>
      </Helmet>
      <Box sx={{ p: { xs: 2, md: 6 }, width: { lg: '1200px' }, maxWidth: '100%', mx: 'auto' }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            gap: 4,
            alignItems: 'flex-start',
          }}
        >
          <Box sx={{ width: '100%', maxWidth: { md: '50%' } }}>
            <Box sx={{ width: '100%', mb: 2 }}>
              <CardMedia
                component="img"
                image={selectedImage}
                alt={product.name}
                sx={{
                  width: '100%',
                  borderRadius: 2,
                  aspectRatio: '1/1',
                  objectFit: 'contain',
                  bgcolor: 'background.paper',
                  border: '1px solid',
                  borderColor: 'grey.200'
                }}
              />
            </Box>

            <Box
              sx={{
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                width: '100%',
              }}
              onMouseOver={() => setIsHovered(true)}
              onMouseOut={() => setIsHovered(false)}
            >
              {isHovered && (
                <>
                  <IconButton
                    onClick={() => handleScroll('left')}
                    onMouseOver={() => setIsHovered(true)}
                    onMouseOut={() => setIsHovered(false)}
                    sx={{
                      position: 'absolute',
                      left: 0,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      zIndex: 2,
                      bgcolor: 'rgba(0, 0, 0, 0.5)',
                      color: 'white',
                      '&:hover': {
                        bgcolor: 'rgba(0, 0, 0, 0.7)',
                      },
                    }}
                  >
                    <KeyboardArrowLeft />
                  </IconButton>
                  <IconButton
                    onClick={() => handleScroll('right')}
                    onMouseOver={() => setIsHovered(true)}
                    onMouseOut={() => setIsHovered(false)}
                    sx={{
                      position: 'absolute',
                      right: 0,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      zIndex: 2,
                      bgcolor: 'rgba(0, 0, 0, 0.5)',
                      color: 'white',
                      '&:hover': {
                        bgcolor: 'rgba(0, 0, 0, 0.7)',
                      },
                    }}
                  >
                    <KeyboardArrowRight />
                  </IconButton>
                </>
              )}
              <Box
                ref={scrollContainerRef}
                sx={{
                  display: 'flex',
                  gap: 2,
                  overflowX: 'auto',
                  position: 'relative',
                  '-ms-overflow-style': 'none',
                  'scrollbar-width': 'none',
                  '&::-webkit-scrollbar': { display: 'none' },
                  width: '100%',
                  justifyContent: 'flex-start',
                  p: 1,
                }}
              >
                {images.map((img, index) => (
                  <Box
                    key={index}
                    onClick={() => setSelectedImage(img.url)}
                    sx={{
                      flexShrink: 0,
                      borderRadius: 2,
                      cursor: 'pointer',
                      border: selectedImage === img.url ? '2px solid' : '1px solid',
                      borderColor: selectedImage === img.url ? 'primary.main' : 'grey.300',
                      transition: 'border-color 0.3s',
                      width: { xs: '60px', md: '80px' },
                      height: { xs: '60px', md: '80px' },
                      boxShadow: selectedImage === img.url ? 3 : 1,
                    }}
                  >
                    <CardMedia
                      component="img"
                      image={img.url}
                      alt={product.name}
                      sx={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        borderRadius: 2,
                      }}
                    />
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>
          <Box sx={{ flex: 1, pr: 4, maxWidth: { md: '50%' } }}>
            <Typography variant="h4" gutterBottom>
              {product.name}
            </Typography>
            <Typography variant="body1" paragraph sx={{ mb: 2 }}>
              {product.description}
            </Typography>
            {product.descriptionHtml && (
              <Typography
                variant="body2"
                dangerouslySetInnerHTML={{ __html: product.descriptionHtml }}
                paragraph
                sx={{ mb: 2 }}
              />
            )}
            <Box component="div" sx={{ mt: 2, mb: 2 }}>
              {product.originalPrice && product.originalPrice > product.price ? (
                <>
                  <Typography
                    variant="h6"
                    component="span"
                    color="text.secondary"
                    sx={{ textDecoration: 'line-through', mr: '0.5rem' }}
                  >
                    List Price: {formatPrice(product.originalPrice)}
                  </Typography>
                  <br/>
                  <Typography variant="h6" component="span"  sx={{ fontWeight: 'bold' }}>
                    Limited-Time Pre-order: {formatPrice(product.price)}
                  </Typography>
                </>
              ) : (
                <Typography variant="h6" component="span"  sx={{ fontWeight: 'bold' }}>
                  {formatPrice(product.price)}
                </Typography>
              )}
            </Box>
            <Chip
              label={stockStatus}
              onClick={() => isAvailableForOrder ? addAndNavigate() : function () { }}
              color={
                stockStatus === 'Out of Stock'
                  ? 'error'
                  : stockStatus === 'Preorder' || stockStatus === 'Backorder'
                    ? 'warning'
                    : stockStatus === 'Digital' || stockStatus === 'Made to Order'
                      ? 'info'
                      : 'success'
              }
              sx={{ mb: 2, cursor: 'pointer' }}
            />
            <Typography variant="body2" sx={{ mb: 2 }}>
              <strong>Availability Date:</strong> {formatDate(product.availabilityDate)}
            </Typography>
            {renderDimensions()}
            <Typography variant="body2" sx={{ mt: 2 }}>
              <strong>Lead Time:</strong> {product.leadTime} days
            </Typography>
            <Box sx={{ mt: 3 }}>
              <Button
                component={Link}
                to="/cart"
                variant="outlined"
                color="primary"
                size="large"
                onClick={() => cartStore.addToCart(product)}
                disabled={!isAvailableForOrder}
              >
                {stockStatus === 'Preorder' ? 'Preorder Today' : 'Add to Cart'}
              </Button>
            </Box>
          </Box>
        </Box>

        <Divider sx={{ my: 4 }} />

        <Box sx={{ maxWidth: '100%', pr: 4 }}>
          {renderList('Key Features', product.specifications?.KeyFeatures)}
          <Divider sx={{ my: 4 }} />
          {renderList('Technical Specifications', product.specifications?.TechnicalSpecifications)}
          <Divider sx={{ my: 4 }} />
          {renderList('Included Accessories', product.specifications?.IncludedAccessories)}
        </Box>

        <Divider sx={{ my: 4 }} />

        <Box sx={{ maxWidth: '100%' }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              justifyContent: 'space-between',
              alignItems: { xs: 'flex-start', sm: 'center' },
              gap: 2,
              mb: 3,
            }}
          >
            <Typography variant="h5">Reviews</Typography>
            <Button
              variant="contained"
              color="primary"
              onClick={() => {
                setReviewSuccess('');
                setReviewError('');
                setShowReviewForm((v) => !v);
              }}
            >
              {showReviewForm ? 'Cancel' : 'Write a review'}
            </Button>
          </Box>

          {reviewSuccess && (
            <Alert severity="success" sx={{ mb: 3 }} onClose={() => setReviewSuccess('')}>
              {reviewSuccess}
            </Alert>
          )}

          <Collapse in={showReviewForm} unmountOnExit>
            <Box
              component="form"
              onSubmit={handleReviewSubmit}
              sx={{
                border: '1px solid',
                borderColor: 'grey.300',
                borderRadius: 2,
                p: { xs: 2, sm: 3 },
                mb: 4,
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
              }}
            >
              <Typography variant="h6">Write a review</Typography>
              {reviewError && (
                <Alert severity="error" onClose={() => setReviewError('')}>
                  {reviewError}
                </Alert>
              )}
              <TextField
                label="Your name"
                required
                value={reviewForm.authorName}
                onChange={(e) => setReviewForm({ ...reviewForm, authorName: e.target.value })}
                size="small"
                fullWidth
              />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2" component="span">
                  Rating
                </Typography>
                <Rating
                  value={reviewForm.rating}
                  onChange={(e, value) => setReviewForm({ ...reviewForm, rating: value || 0 })}
                />
              </Box>
              <TextField
                label="Title"
                value={reviewForm.title}
                onChange={(e) => setReviewForm({ ...reviewForm, title: e.target.value })}
                size="small"
                fullWidth
              />
              <TextField
                label="Review"
                value={reviewForm.body}
                onChange={(e) => setReviewForm({ ...reviewForm, body: e.target.value })}
                multiline
                minRows={3}
                fullWidth
              />
              <Box>
                <Button type="submit" variant="contained" color="primary" disabled={submitting}>
                  {submitting ? 'Submitting…' : 'Submit review'}
                </Button>
              </Box>
            </Box>
          </Collapse>

          {reviewsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={28} />
            </Box>
          ) : reviewSummary.count > 0 ? (
            <>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  gap: { xs: 2, sm: 6 },
                  alignItems: { sm: 'center' },
                  mb: 4,
                }}
              >
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Rating value={reviewSummary.average} precision={0.5} readOnly />
                    <Typography variant="h6" component="span">
                      {Number(reviewSummary.average).toFixed(1)}
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    ({reviewSummary.count} {reviewSummary.count === 1 ? 'review' : 'reviews'})
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, flex: 1, maxWidth: 320, width: '100%' }}>
                  {[5, 4, 3, 2, 1].map((star) => {
                    const dist = reviewSummary.distribution || {};
                    const starCount = dist[star] || 0;
                    const pct = reviewSummary.count > 0 ? (starCount / reviewSummary.count) * 100 : 0;
                    return (
                      <Box key={star} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ width: 28, flexShrink: 0 }}>
                          {star}★
                        </Typography>
                        <Box
                          sx={{
                            flex: 1,
                            height: 8,
                            borderRadius: 1,
                            bgcolor: 'grey.200',
                            overflow: 'hidden',
                          }}
                        >
                          <Box sx={{ width: `${pct}%`, height: '100%', bgcolor: 'primary.main' }} />
                        </Box>
                        <Typography variant="caption" color="text.secondary" sx={{ width: 24, flexShrink: 0, textAlign: 'right' }}>
                          {starCount}
                        </Typography>
                      </Box>
                    );
                  })}
                </Box>
              </Box>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {reviews.map((review) => (
                  <Box
                    key={review._id}
                    sx={{
                      border: '1px solid',
                      borderColor: 'grey.300',
                      borderRadius: 2,
                      p: { xs: 2, sm: 2.5 },
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                      <Rating value={review.rating} precision={0.5} readOnly size="small" />
                      {review.verified && (
                        <Chip label="Verified" color="success" size="small" variant="outlined" />
                      )}
                    </Box>
                    {review.title && (
                      <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                        {review.title}
                      </Typography>
                    )}
                    {review.body && (
                      <Typography variant="body2" sx={{ mt: 0.5, mb: 1 }}>
                        {review.body}
                      </Typography>
                    )}
                    <Typography variant="caption" color="text.secondary">
                      {review.authorName || 'Anonymous'} · {formatDate(review.createdAt)}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </>
          ) : (
            <Typography variant="body1" color="text.secondary">
              No reviews yet — be the first.
            </Typography>
          )}
        </Box>
      </Box>
    </PageLayout>
  );
});

export default ProductDetailsPage;
